import { c as _c } from "react/compiler-runtime";
import { Box } from "@mui/material";
import React, { useMemo } from "react";
import { ArgumentType } from "#/entities/argument/model/argumentObject";
import { useBinaryTreePositioning } from "#/entities/dataStructures/binaryTree/hooks/useBinaryTreePositioning";
import { BinaryNode } from "#/entities/dataStructures/binaryTree/ui/BinaryNode";
import { GraphEdge } from "#/entities/dataStructures/graph/ui/GraphEdge";
import { GraphNode } from "#/entities/dataStructures/graph/ui/GraphNode";
import { LinkedListNode } from "#/entities/dataStructures/linkedList/ui/LinkedListNode";
import { selectMinXOffset, type TreeData } from "#/entities/dataStructures/node/model/nodeSlice";
import { selectDisableLayoutTransitions } from "#/features/treeViewer/model/editorSlice";
import { useAppSelector } from "#/store/hooks";
const nodeComponentMap = {
  [ArgumentType.LINKED_LIST]: LinkedListNode,
  [ArgumentType.BINARY_TREE]: BinaryNode,
  [ArgumentType.GRAPH]: GraphNode
};
type NodesViewProps = {
  treeName: string;
  data: TreeData;
  style?: React.CSSProperties;
  sx?: React.CSSProperties;
  horizontalAlign?: "start" | "center";
};
const NODE_WIDTH = 42;
export const NodesView: React.FC<NodesViewProps> = t0 => {
  const $ = _c(32);
  const {
    treeName,
    data,
    style,
    sx,
    horizontalAlign: t1
  } = t0;
  const horizontalAlign = t1 === undefined ? "start" : t1;
  const adjustXOffset = data.type === ArgumentType.LINKED_LIST;
  const offset = useAppSelector(selectMinXOffset(treeName, adjustXOffset)) ?? 0;
  const disableLayoutTransitions = useAppSelector(selectDisableLayoutTransitions);
  useBinaryTreePositioning(treeName, data);
  const Node = nodeComponentMap[data.type];
  style?.left;
  let t2;
  if ($[0] !== data.nodes.entities || $[1] !== data.rootId || $[2] !== data.type || $[3] !== horizontalAlign || $[4] !== offset || $[5] !== style?.left) {
    bb0: {
      const baseLeft = (Number(style?.left) || 0) - offset;
      if (horizontalAlign !== "center" || data.type !== ArgumentType.BINARY_TREE) {
        t2 = baseLeft;
        break bb0;
      }
      const nodes = Object.values(data.nodes.entities).filter(_temp);
      if (nodes.length === 0) {
        t2 = baseLeft;
        break bb0;
      }
      const rootNode = data.rootId ? data.nodes.entities[data.rootId] : undefined;
      const anchorCenterX = rootNode ? rootNode.x + NODE_WIDTH / 2 : (() => {
        const minX = Math.min(...nodes.map(_temp2));
        const maxX = Math.max(...nodes.map(_temp3));
        return (minX + maxX + NODE_WIDTH) / 2;
      })();
      if (baseLeft === 0) {
        t2 = `calc(50% - ${anchorCenterX}px)`;
        break bb0;
      }
      const leftSign = baseLeft >= 0 ? "+" : "-";
      let t3;
      if ($[7] !== baseLeft) {
        t3 = Math.abs(baseLeft);
        $[7] = baseLeft;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      t2 = `calc(50% ${leftSign} ${t3}px - ${anchorCenterX}px)`;
    }
    $[0] = data.nodes.entities;
    $[1] = data.rootId;
    $[2] = data.type;
    $[3] = horizontalAlign;
    $[4] = offset;
    $[5] = style?.left;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const left = t2;
  let t3;
  if ($[9] !== sx) {
    t3 = {
      position: "absolute",
      width: "100%",
      height: "100%",
      ...sx
    };
    $[9] = sx;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = disableLayoutTransitions ? "none" : "left 0.05s ease-in-out";
  let t5;
  if ($[11] !== left || $[12] !== style || $[13] !== t4) {
    t5 = {
      ...style,
      left,
      transition: t4
    };
    $[11] = left;
    $[12] = style;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== data.edges.entities) {
    t6 = Object.values(data.edges.entities);
    $[15] = data.edges.entities;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t6 || $[18] !== treeName) {
    t7 = t6.map(edge => edge && <GraphEdge key={edge.id} treeName={treeName} {...edge} />);
    $[17] = t6;
    $[18] = treeName;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== data.nodes.entities) {
    t8 = Object.values(data.nodes.entities);
    $[20] = data.nodes.entities;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== Node || $[23] !== data.type || $[24] !== t8 || $[25] !== treeName) {
    t9 = t8.map(node_2 => node_2 && <Node key={node_2.id} treeName={treeName} type={data.type} {...node_2} />);
    $[22] = Node;
    $[23] = data.type;
    $[24] = t8;
    $[25] = treeName;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t3 || $[28] !== t5 || $[29] !== t7 || $[30] !== t9) {
    t10 = <Box sx={t3} style={t5}>{t7}{t9}</Box>;
    $[27] = t3;
    $[28] = t5;
    $[29] = t7;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
};
function _temp(node) {
  return node !== undefined;
}
function _temp2(node_0) {
  return node_0.x;
}
function _temp3(node_1) {
  return node_1.x;
}